import { useState, useEffect, useCallback } from 'react';
import { useDataCache } from '../contexts/BreakdownDataCacheContext';
import { getRestrictedDatesAsDateStrings } from '../types/element.types';

/**
 * 根据缓存中所有 element 的 elementRestrictedDates（受限日期）数据，
 * 构建「日期 -> 该日有受限日期的主要角色（CAST）elementID 集合」的映射。
 * 用于在拍摄计划页显示受限日期标识。
 */
export function useRestrictedDatesCast(breakdownID: string | undefined) {
  const { getElementCache, subscribeElementCache } = useDataCache();
  const [restrictedDatesCastIdsByDate, setRestrictedDatesCastIdsByDate] = useState<
    Map<string, Set<string>>
  >(new Map());

  const updateMap = useCallback(() => {
    if (!breakdownID) {
      setRestrictedDatesCastIdsByDate(new Map());
      return;
    }
    const cache = getElementCache(breakdownID);
    const elements = cache?.elements ?? [];
    const map = new Map<string, Set<string>>();
    for (const item of elements) {
      const dates = getRestrictedDatesAsDateStrings(item.elementRestrictedDates);
      if (dates.length === 0) continue;
      const id = item.elementID;
      if (!id) continue;
      for (const dateStr of dates) {
        if (!dateStr || dateStr.length < 10) continue;
        let set = map.get(dateStr);
        if (!set) {
          set = new Set<string>();
          map.set(dateStr, set);
        }
        set.add(id);
      }
    }
    setRestrictedDatesCastIdsByDate(map);
  }, [breakdownID, getElementCache]);

  useEffect(() => {
    updateMap();
    if (!breakdownID) return;
    const unsub = subscribeElementCache(breakdownID, updateMap);
    return unsub;
  }, [breakdownID, updateMap, subscribeElementCache]);

  const getRestrictedDatesCastIdsForDate = useCallback(
    (dateStr: string): Set<string> => {
      return restrictedDatesCastIdsByDate.get(dateStr) ?? new Set();
    },
    [restrictedDatesCastIdsByDate]
  );

  return { restrictedDatesCastIdsByDate, getRestrictedDatesCastIdsForDate };
}
